import { TouchEvent, useRef } from "react";

const MOBILE_MAX_WIDTH = 743;
const DISMISS_DISTANCE = 80;

const findScrollableParent = (
  target: HTMLElement | null,
  boundary: HTMLElement
) => {
  let element = target;

  while (element) {
    if (element.scrollHeight > element.clientHeight) {
      const overflowY = window.getComputedStyle(element).overflowY;
      if (overflowY === "auto" || overflowY === "scroll") return element;
    }

    if (element === boundary) break;
    element = element.parentElement;
  }

  return null;
};

const useSwipeToDismiss = (onDismiss?: () => void, enabled = true) => {
  const sheetRef = useRef<HTMLDivElement>(null);
  const gesture = useRef({
    active: false,
    startX: 0,
    startY: 0,
    distance: 0,
  });

  const resetPosition = () => {
    const sheet = sheetRef.current;
    if (!sheet) return;

    sheet.style.transition = "transform 180ms ease-out";
    sheet.style.transform = "translate3d(0, 0, 0)";
  };

  const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    if (!enabled || window.innerWidth > MOBILE_MAX_WIDTH) return;

    const sheet = sheetRef.current;
    const touch = event.touches[0];
    if (!sheet || !touch) return;

    const scrollableParent = findScrollableParent(
      event.target as HTMLElement,
      sheet
    );
    if (scrollableParent && scrollableParent.scrollTop > 0) return;

    sheet.style.transition = "none";
    gesture.current = {
      active: true,
      startX: touch.clientX,
      startY: touch.clientY,
      distance: 0,
    };
  };

  const handleTouchMove = (event: TouchEvent<HTMLDivElement>) => {
    if (!gesture.current.active) return;

    const touch = event.touches[0];
    if (!touch) return;

    const distanceY = touch.clientY - gesture.current.startY;
    const distanceX = Math.abs(touch.clientX - gesture.current.startX);

    if (distanceY <= 0 || distanceX > distanceY) {
      gesture.current.active = false;
      resetPosition();
      return;
    }

    event.preventDefault();
    gesture.current.distance = distanceY;

    if (sheetRef.current) {
      sheetRef.current.style.transform = `translate3d(0, ${distanceY}px, 0)`;
    }
  };

  const handleTouchEnd = () => {
    if (!gesture.current.active) return;

    const shouldDismiss = gesture.current.distance >= DISMISS_DISTANCE;
    gesture.current.active = false;

    if (shouldDismiss) {
      onDismiss?.();
      return;
    }

    resetPosition();
  };

  return {
    sheetRef,
    swipeHandlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
      onTouchCancel: handleTouchEnd,
    },
  };
};

export default useSwipeToDismiss;
